/**
 * Testimonials Component
 *
 * Customer testimonials grid or carousel (if more than 6 items)
 *
 * Following CLAUDE.md:
 * - Uses Tailwind CSS utility classes for styling
 * - Brand-agnostic (no brand logic in components)
 * - Uses cn() utility for conditional classes
 * - Mobile-first responsive design
 * - Uses Shadcn Card component
 * - Uses Shadcn Carousel for large sets
 */

"use client";

import * as React from "react";

/**
 * Simple hash function for generating stable keys
 */
function hashString(str: string): string {
	let hash = 0;
	for (let i = 0; i < str.length; i++) {
		const char = str.charCodeAt(i);
		hash = (hash << 5) - hash + char;
		hash = hash & hash; // Convert to 32bit integer
	}
	return Math.abs(hash).toString(36);
}

import {
	Card,
	CardDescription,
	CardFooter,
	CardHeader,
} from "@/components/ui/card";
import {
	Carousel,
	type CarouselApi,
	CarouselContent,
	CarouselItem,
	CarouselNext,
	CarouselPrevious,
} from "@/components/ui/carousel";
import { cn } from "@/lib/utils";
import type { ComponentTestimonials } from "@/lib/validation";

export interface TestimonialsProps extends ComponentTestimonials {
	className?: string;
}

export function Testimonials({
	className,
	heading,
	testimonials,
}: TestimonialsProps) {
	const [api, setApi] = React.useState<CarouselApi>();

	if (!testimonials || testimonials.length === 0) return null;

	return (
		<section className={cn("bg-muted/50 py-12 md:py-16 lg:py-20", className)}>
			<div className="container mx-auto px-4">
				{/* Section Header */}
				{heading && (
					<h2 className="mb-12 text-center text-2xl font-bold md:text-3xl lg:text-4xl">
						{heading}
					</h2>
				)}

				{/* Testimonials Carousel (always) */}
				<Carousel
					opts={{
						align: "start",
						loop: true,
					}}
					setApi={setApi}
					className="mx-auto w-full"
				>
					{/* Mobile-only nav row — sits above slides */}
					<div className="mb-4 flex justify-end gap-2 md:hidden">
						<button
							type="button"
							onClick={() => api?.scrollPrev()}
							className="flex size-8 items-center justify-center rounded-full border bg-background"
							aria-label="Previous slide"
						>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								width="16"
								height="16"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								strokeWidth="2"
								strokeLinecap="round"
								strokeLinejoin="round"
								aria-hidden="true"
							>
								<path d="m15 18-6-6 6-6" />
							</svg>
						</button>
						<button
							type="button"
							onClick={() => api?.scrollNext()}
							className="flex size-8 items-center justify-center rounded-full border bg-background"
							aria-label="Next slide"
						>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								width="16"
								height="16"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								strokeWidth="2"
								strokeLinecap="round"
								strokeLinejoin="round"
								aria-hidden="true"
							>
								<path d="m9 18 6-6-6-6" />
							</svg>
						</button>
					</div>

					<CarouselContent className="-ml-4">
						{testimonials.map((testimonial, index) => (
							<CarouselItem
								key={hashString(
									`${testimonial.author}-${testimonial.text}-${index}`,
								)}
								className="pl-4 md:basis-1/2 lg:basis-1/3"
							>
								<Card className="flex flex-col md:h-full">
									<CardHeader>
										<CardDescription className="text-base italic leading-relaxed">
											&ldquo;{testimonial.text}&rdquo;
										</CardDescription>
									</CardHeader>
									{testimonial.author && (
										<CardFooter className="mt-auto">
											<p className="font-semibold text-foreground">
												&mdash; {testimonial.author}
											</p>
										</CardFooter>
									)}
								</Card>
							</CarouselItem>
						))}
					</CarouselContent>
					<CarouselPrevious />
					<CarouselNext />
				</Carousel>
			</div>
		</section>
	);
}
